import { useLocation, useNavigate } from '@tanstack/react-router';
import { SideMenuItem, SideMenu as VCDKSideMenu } from '@volvo/vcdk-react';
import { useTranslation } from 'react-i18next';

import { withComponentErrorBoundary } from '@/components/ComponentErrorBoundary';
import { analyticsEvents } from '@/contexts/analytics/events';
import { useFleetAccount } from '@/contexts/fleet-account/useFleetAccount';
import useMediaQuery from '@/hooks/useMediaQuery';

type SideMenuProps = {
  isOpen: boolean;
};

const SideMenu: React.FC<SideMenuProps> = ({ isOpen }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { mediaQueryNodes: mq } = useMediaQuery();
  const { fleetAccounts } = useFleetAccount();
  const hasFleetAccount = fleetAccounts && fleetAccounts.length > 0;

  const isActivePath = (paths: string[]) =>
    paths.some((path) => location.pathname === path || location.pathname.startsWith(`${path}/`));

  const menuMode = mq({
    default: 'modal' as const,
    sm: 'permanent' as const,
  });

  return (
    <VCDKSideMenu mode={menuMode} collapse={menuMode === 'permanent' ? !isOpen : true} open={isOpen}>
      <SideMenuItem
        icon="home"
        active={!isActivePath(['/settings', '/support'])}
        onClick={() => {
          analyticsEvents.Interaction.componentInteraction({
            parent: 'SideMenu',
            type: 'SideMenuItem',
            title: 'Home',
            href: '/dashboard',
            placement: 'SideMenu',
            selectedOptions: [
              {
                attribute: 'name',
                value: 'home',
              },
            ],
          });
          navigate({ to: '/dashboard' });
        }}
      >
        {t(($) => $.components.main_menu.home)}
      </SideMenuItem>

      {hasFleetAccount && (
        <SideMenuItem
          icon="settings"
          active={isActivePath(['/settings'])}
          onClick={() => {
            analyticsEvents.Interaction.componentInteraction({
              parent: 'SideMenu',
              type: 'SideMenuItem',
              title: 'Settings',
              href: '/settings',
              placement: 'SideMenu',
              selectedOptions: [
                {
                  attribute: 'name',
                  value: 'settings',
                },
              ],
            });
            navigate({ to: '/settings' });
          }}
        >
          {t(($) => $.components.main_menu.settings)}
        </SideMenuItem>
      )}

      <SideMenuItem
        icon="info"
        active={isActivePath(['/support'])}
        onClick={() => {
          analyticsEvents.Interaction.componentInteraction({
            parent: 'SideMenu',
            type: 'SideMenuItem',
            title: 'Support',
            href: '/support',
            placement: 'SideMenu',
            selectedOptions: [
              {
                attribute: 'name',
                value: 'support',
              },
            ],
          });
          navigate({ to: '/support' });
        }}
      >
        {t(($) => $.components.main_menu.support)}
      </SideMenuItem>
    </VCDKSideMenu>
  );
};

export default withComponentErrorBoundary(SideMenu);
